import { OPPORTUNITY_SETUP_PLACEMENT, type CurrentInsightSetup } from '@testfeed/shopify-contracts'
import { insightOpportunityCopy as copy } from '@testfeed/product-language'
import type { RenderPage } from '../../insightRenderPresentation'
import { InsightPageRenderer } from './cards/InsightPageRenderer'
import type { InsightListContent } from './cards/insightListContent'
import { InsightOpportunityCard } from './InsightOpportunityCard'
import { InsightOpportunityDetails } from './InsightOpportunityDetails'
import { useInsightOpportunityDetails } from './useInsightOpportunityDetails'
import { useInsightOpportunityAction } from './useInsightOpportunityAction'
import type { TestInsightSetup } from './InsightSetupsSection'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../shopifyUiTracking'
import type { InsightsLifecycle } from '../../pages/insights.page'

export type InsightSetupsState = { status: 'loading' | 'error' } | { status: 'ready'; setups: CurrentInsightSetup[] }

/** Actual production composition, also used by Ladle. No transport, generation or analytical calculations here. */
export function InsightOpportunities({
  page,
  revision,
  state,
  onTestSetup,
  lifecycle,
}: {
  page: RenderPage
  revision: string
  state: InsightSetupsState
  onTestSetup: TestInsightSetup
  lifecycle: InsightsLifecycle
}) {
  const shopify = useAppBridge()
  const action = useInsightOpportunityAction(onTestSetup, lifecycle)
  const details = useInsightOpportunityDetails()
  const available: CurrentInsightSetup[] = []
  async function test() {
    if (details.session && (await action.test(details.session)) === 'saved') details.closeAfterSave()
  }
  const { sectionId, listId } = OPPORTUNITY_SETUP_PLACEMENT
  const group = page.sections.find((section) => section.id === sectionId)
  const list = group?.kind === 'list-group' ? group.lists.find((item) => item.id === listId) : undefined
  const content: InsightListContent = { [sectionId]: { [listId]: { rows: {} } } }
  const slot = content[sectionId]![listId]!
  let notice: string | null = null
  if (state.status === 'loading') notice = copy.loading
  else if (state.status === 'error') notice = copy.readFailed
  else if (state.status === 'ready') {
    for (const setup of state.setups) {
      const source = setup.source
      const matches =
        revision &&
        source.revision === revision &&
        source.sectionId === sectionId &&
        source.listId === listId &&
        source.rowId === setup.productId &&
        list?.state === 'ready' &&
        list.rows.some((row) => row.id === source.rowId)
      if (!matches) {
        notice = copy.refresh
        continue
      }
      available.push(setup)
      let error: string | undefined
      const failure = action.failure
      if (failure && failure.batchId === setup.batchId) error = failure.message
      slot.rows![source.rowId] = (
        <InsightOpportunityCard
          key={setup.batchId}
          setup={setup}
          pending={action.pending}
          error={error}
          onDetails={(setup, trigger) =>
            trackShopifyAction(shopify, 'open-opportunity', () => details.show(setup, trigger))
          }
        />
      )
    }
  }
  let outsideNotice = null
  if (notice) {
    const message = (
      <div role="status">
        <s-paragraph color="subdued">{notice}</s-paragraph>
      </div>
    )
    if (list) slot.footer = message
    else outsideNotice = message
  }
  const selectedIsAvailable = available.some((setup) => setup.batchId === details.setup?.batchId)
  let detailError: string | undefined
  if (action.failure && action.failure.batchId === details.setup?.batchId) detailError = action.failure.message
  return (
    <s-stack gap="base">
      <InsightPageRenderer page={page} listContent={content} />
      {outsideNotice}
      <InsightOpportunityDetails
        setup={details.setup}
        open={details.open}
        available={selectedIsAvailable}
        lifecycle={action.lifecycle}
        error={detailError}
        onAfterHide={details.afterHide}
        onTest={() => {
          void test()
        }}
      />
    </s-stack>
  )
}
